import { useState } from "react";
import { asJson, isBranch, isEmpty, rawJson } from "@/lib/json";
import { CopyButton } from "./copy-button";
import { JsonTree } from "./json-tree";

type Mode = "tree" | "raw";

function hasEmpty(value: unknown): boolean {
  return isBranch(value) && Object.values(value).some((v) => isEmpty(v) || hasEmpty(v));
}

function Toggle({ on, onClick, children }: { on: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`rounded-[5px] px-2 py-0.5 text-[11px] font-semibold ${
        on ? "bg-surface text-ink shadow-[0_1px_2px_rgba(24,25,29,0.12)]" : "text-muted hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

/** Text, or a value too small for a tree, with its own line breaks. */
function Plain({ value, label, framed }: { value: unknown; label: string; framed: boolean }) {
  const none = value === null || value === undefined || value === "";
  return (
    <pre
      aria-label={label}
      className={`m-0 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap
                  ${none ? "text-dim italic" : "text-code"} ${framed ? "rounded-lg border border-line-soft bg-well px-3.5 py-2.5" : ""}`}
    >
      {none ? (value === "" ? '""' : "null") : rawJson(value)}
    </pre>
  );
}

export function JsonView({
  value,
  label,
  onPick,
  root = "",
  openDepth = 2,
  hideEmptyByDefault = false,
  toolbar = true,
  className = "",
}: {
  value: unknown;
  /** The accessible name of the tree, the raw text or the plain text. */
  label: string;
  onPick?: (path: string) => void;
  /** Path of `value` itself, prefixed to every row's path. */
  root?: string;
  openDepth?: number;
  hideEmptyByDefault?: boolean;
  /** False: the bare tree, for a value nested in another layout. */
  toolbar?: boolean;
  /** Extra classes for the body, e.g. a max height. */
  className?: string;
}) {
  const [mode, setMode] = useState<Mode>("tree");
  const [hideEmpty, setHideEmpty] = useState(hideEmptyByDefault);
  const v = asJson(value);
  if (!isBranch(v) || isEmpty(v)) return <Plain value={v} label={label} framed={toolbar} />;

  const tree = <JsonTree value={v} label={label} opts={{ onPick, root, hideEmpty, openDepth }} />;
  if (!toolbar) return tree;
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-line-soft bg-well">
      <div className="flex items-center gap-2 border-b border-line-soft px-2 py-1">
        <div role="group" aria-label={`${label} view`} className="flex gap-0.5 rounded-md bg-chip p-0.5">
          <Toggle on={mode === "tree"} onClick={() => setMode("tree")}>
            Tree
          </Toggle>
          <Toggle on={mode === "raw"} onClick={() => setMode("raw")}>
            Raw
          </Toggle>
        </div>
        <span className="flex-1" />
        {mode === "tree" && hasEmpty(v) && (
          <button
            type="button"
            aria-pressed={hideEmpty}
            className={`rounded-[5px] px-1.5 py-0.5 text-[11px] hover:bg-chip hover:text-ink ${
              hideEmpty ? "bg-chip text-ink" : "text-muted"
            }`}
            onClick={() => setHideEmpty(!hideEmpty)}
          >
            Hide empty
          </button>
        )}
        <CopyButton text={rawJson(v)} label={`Copy ${label}`} />
      </div>
      <div className={`overflow-auto px-2 py-2 ${className}`}>
        {mode === "tree" ? (
          tree
        ) : (
          <pre
            aria-label={label}
            className="m-0 px-1 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap text-code"
          >
            {rawJson(v)}
          </pre>
        )}
      </div>
    </div>
  );
}
